import type { LocatesV2WidgetModel } from '@thinkalpha/ui-types/contracts/workspace';
import {
    DEFAULT_COLUMN_ORDER,
    DEFAULT_COLUMN_VISIBILITY,
    DEFAULT_COLUMN_WIDTHS,
} from 'src/routes/widgets/LocatesV2Widget/columns';
import type { LocatesV2Actions } from 'src/store/actions/widgets/locatesV2';
import type { WidgetTabViewModel } from 'src/store/types';

export const locatesV2Reducer = (
    state: WidgetTabViewModel<LocatesV2WidgetModel>,
    action: LocatesV2Actions,
): WidgetTabViewModel<LocatesV2WidgetModel> => {
    switch (action.type) {
        case 'updateLocatesV2Account': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    accountIds: action.accountId ? [action.accountId] : [],
                },
            };
        }
        case 'updateLocatesV2Symbol':
        case 'userSelectedNewLocatesV2Symbol': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    formData: {
                        ...state.widget.formData,
                        symbol: action.symbol,
                    },
                },
            };
        }
        case 'setLocatesV2DisplayingQuote': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    displayingQuote: action.isShowing,
                },
            };
        }
        case 'setLocatesV2DisplayingResults': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    displayingResults: action.isShowing,
                },
            };
        }
        case 'setLocatesV2DisplayingCostInfo': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    displayingCostInfo: action.isShowing,
                },
            };
        }
        case 'storeLocatesV2SplitPercentage': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    splitPercentage: action.splitPercentage,
                },
            };
        }
        case 'updateLocatesV2FormData': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    formData: action.formData,
                },
            };
        }
        case 'updateLocatesV2QuickSendButtons': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    quickSendButtons: action.buttons,
                },
            };
        }
        case 'setLocatesV2SoundAlert': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    isSoundAlertEnabled: action.enabled,
                },
            };
        }
        case 'setLocatesV2QuantityKilo': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    isQuantityKilo: action.enabled,
                },
            };
        }
        case 'setLocatesV2UserOnly': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    isUserOnly: action.enabled,
                },
            };
        }
        case 'setLocatesV2OnlyPending': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    onlyPending: action.enabled,
                    // Mutual exclusivity: only one tab filter active at a time
                    onlyResults: action.enabled ? false : state.widget.onlyResults,
                    onlyUserActivity: action.enabled ? false : state.widget.onlyUserActivity,
                },
            };
        }
        case 'setLocatesV2OnlyResults': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    onlyResults: action.enabled,
                    // Mutual exclusivity: only one tab filter active at a time
                    onlyPending: action.enabled ? false : state.widget.onlyPending,
                    onlyUserActivity: action.enabled ? false : state.widget.onlyUserActivity,
                },
            };
        }
        case 'setLocatesV2OnlyUserActivity': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    onlyUserActivity: action.enabled,
                    // Mutual exclusivity: only one tab filter active at a time
                    onlyPending: action.enabled ? false : state.widget.onlyPending,
                    onlyResults: action.enabled ? false : state.widget.onlyResults,
                },
            };
        }
        case 'setLocatesV2AccountIds': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    accountIds: action.accountIds,
                },
            };
        }
        case 'updateLocatesV2CostInfoColumnTemplate': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    costInfoTableData: {
                        ...state.widget.costInfoTableData,
                        columnTemplate: action.columnTemplate,
                    },
                },
            };
        }
        case 'updateLocatesV2Filter': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    filter: action.filter,
                },
            };
        }
        case 'updateLocatesV2ColumnWidths': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    resultsTableColumns: {
                        ...state.widget.resultsTableColumns,
                        columnOrder: state.widget.resultsTableColumns?.columnOrder ?? DEFAULT_COLUMN_ORDER,
                        columnWidths: Object.keys(action.columnWidths).length
                            ? action.columnWidths
                            : (state.widget.resultsTableColumns?.columnWidths ?? DEFAULT_COLUMN_WIDTHS),
                        columnVisibility:
                            state.widget.resultsTableColumns?.columnVisibility ?? DEFAULT_COLUMN_VISIBILITY,
                    },
                },
            };
        }
        case 'updateLocatesV2ColumnOrder': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    resultsTableColumns: {
                        ...state.widget.resultsTableColumns,
                        columnOrder: action.columnOrder.length ? action.columnOrder : DEFAULT_COLUMN_ORDER,
                        columnWidths: state.widget.resultsTableColumns?.columnWidths ?? DEFAULT_COLUMN_WIDTHS,
                        columnVisibility:
                            state.widget.resultsTableColumns?.columnVisibility ?? DEFAULT_COLUMN_VISIBILITY,
                    },
                },
            };
        }
        case 'updateLocatesV2ColumnVisibility': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    resultsTableColumns: {
                        ...state.widget.resultsTableColumns,
                        columnOrder: state.widget.resultsTableColumns?.columnOrder ?? DEFAULT_COLUMN_ORDER,
                        columnWidths: state.widget.resultsTableColumns?.columnWidths ?? DEFAULT_COLUMN_WIDTHS,
                        columnVisibility: Object.keys(action.columnVisibility).length
                            ? action.columnVisibility
                            : DEFAULT_COLUMN_VISIBILITY,
                    },
                },
            };
        }
        case 'updateLocatesV2Sort': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    resultsTableColumns: {
                        ...state.widget.resultsTableColumns,
                        columnOrder: state.widget.resultsTableColumns?.columnOrder ?? DEFAULT_COLUMN_ORDER,
                        columnWidths: state.widget.resultsTableColumns?.columnWidths ?? DEFAULT_COLUMN_WIDTHS,
                        columnVisibility:
                            state.widget.resultsTableColumns?.columnVisibility ?? DEFAULT_COLUMN_VISIBILITY,
                        currentSort: action.sort,
                    },
                },
            };
        }
        case 'setLocatesV2AutoAcceptType': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    autoAcceptType: action.autoAcceptType,
                },
            };
        }
        case 'setLocatesV2AutoAcceptPercent': {
            return {
                ...state,
                widget: {
                    ...state.widget,
                    autoAcceptPercent: action.autoAcceptPercent,
                },
            };
        }

        default:
            return state;
    }
};
